/**
 * OPS-STAB-003 — fast print open (both shells).
 */
const blobUrlCache = new Map();

/**
 * @param {Blob} blob
 * @param {string} [title]
 * @returns {{ opened: boolean; popupBlocked: boolean; downloadFallback: boolean }}
 */
export function openPrintBlobFast(blob, title = 'document') {
  if (!blob || typeof window === 'undefined') {
    return { opened: false, popupBlocked: false, downloadFallback: false };
  }
  const prev = blobUrlCache.get(title);
  if (prev) {
    try {
      URL.revokeObjectURL(prev);
    } catch {
      /* ignore */
    }
  }
  const url = URL.createObjectURL(blob);
  blobUrlCache.set(title, url);
  const win = window.open(url, '_blank', 'noopener,noreferrer');
  let downloadFallback = false;
  if (!win) {
    downloadFallback = true;
    const a = document.createElement('a');
    a.href = url;
    a.download = `${title}.pdf`;
    a.rel = 'noopener';
    a.click();
  }
  window.setTimeout(() => {
    try {
      URL.revokeObjectURL(url);
    } catch {
      /* ignore */
    }
    blobUrlCache.delete(title);
  }, 90_000);
  return {
    opened: true,
    popupBlocked: !win,
    downloadFallback,
  };
}

export const LAB_PRINT_STATUS_PREPARING = 'Preparing lab report…';
export const LAB_PRINT_STATUS_FINALIZING = 'Finalizing print preview…';

const PREPARING_MARKUP = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>Preparing lab report</title>
<style>
  html, body { margin: 0; height: 100%; }
  body {
    display: flex; align-items: center; justify-content: center;
    background: #f4f7fb; color: #1f2937;
    font-family: "Segoe UI", Helvetica, Arial, sans-serif;
  }
  .card { text-align: center; padding: 32px; }
  .spinner {
    width: 36px; height: 36px; margin: 0 auto 16px;
    border: 3px solid #dbe3ee; border-top-color: #1d4ed8;
    border-radius: 50%; animation: lab-print-spin 0.8s linear infinite;
  }
  p { margin: 0; font-size: 16px; }
  @keyframes lab-print-spin { to { transform: rotate(360deg); } }
  @media (prefers-reduced-motion: reduce) {
    .spinner { animation: none; border-top-color: #1d4ed8; }
  }
</style>
</head>
<body>
  <div class="card" role="status" aria-live="polite">
    <div class="spinner" aria-hidden="true"></div>
    <p id="lab-print-status">${LAB_PRINT_STATUS_PREPARING}</p>
  </div>
</body>
</html>`;

function paintPreparingScreen(printWindow) {
  const doc = printWindow.document;
  doc.open();
  doc.write(PREPARING_MARKUP);
  doc.close();
}

/**
 * Open a same-origin print window during the click, before any await.
 * `noopener` is omitted so the caller can reach the window and call print().
 * @returns {{ window: Window | null, popupBlocked: boolean }}
 */
export function reserveBrowserPrintWindow() {
  if (typeof window === 'undefined' || typeof window.open !== 'function') {
    return { window: null, popupBlocked: true };
  }
  const printWindow = window.open('about:blank', '_blank');
  if (!printWindow) {
    return { window: null, popupBlocked: true };
  }
  try {
    paintPreparingScreen(printWindow);
  } catch {
    /* The window is still usable for the PDF. */
  }
  return { window: printWindow, popupBlocked: false };
}

/** Updates the preparation message. Does nothing once the PDF document has replaced it. */
export function updateBrowserPrintStatus(printWindow, message) {
  if (!message) return;
  try {
    const node = printWindow?.document?.getElementById('lab-print-status');
    if (node) node.textContent = message;
  } catch {
    /* The preview may already have replaced the preparation screen. */
  }
}

export function closeBrowserPrintWindow(printWindow) {
  try {
    if (printWindow && !printWindow.closed) printWindow.close();
  } catch {
    /* ignore */
  }
}

/**
 * Show a PDF blob in a reserved window and invoke the browser print dialog.
 * Does not create a download link.
 * @param {Window | null | undefined} printWindow
 * @param {Blob} blob
 * @returns {Promise<{ opened: boolean, popupBlocked: boolean, downloadFallback: boolean, loadFailed?: boolean, printed?: boolean }>}
 */
export function showPdfForBrowserPrint(printWindow, blob) {
  if (!printWindow || printWindow.closed) {
    return Promise.resolve({ opened: false, popupBlocked: true, downloadFallback: false });
  }
  if (!blob) {
    closeBrowserPrintWindow(printWindow);
    return Promise.resolve({ opened: false, popupBlocked: false, downloadFallback: false, loadFailed: true });
  }
  let url;
  try {
    const pdfBlob =
      typeof Blob !== 'undefined' && blob.type === 'application/pdf'
        ? blob
        : new Blob([blob], { type: 'application/pdf' });
    url = URL.createObjectURL(pdfBlob);
    const markup = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Print</title>
<style>html,body{margin:0;height:100%;background:#fff}iframe{border:0;width:100%;height:100%}</style>
</head><body><iframe id="lab-print-frame" src="${url}"></iframe></body></html>`;
    printWindow.document.open();
    printWindow.document.write(markup);
    printWindow.document.close();
  } catch {
    closeBrowserPrintWindow(printWindow);
    if (url) {
      try {
        URL.revokeObjectURL(url);
      } catch {
        /* ignore */
      }
    }
    return Promise.resolve({ opened: false, popupBlocked: false, downloadFallback: false, loadFailed: true });
  }

  const frame = printWindow.document.getElementById('lab-print-frame');
  let started = false;
  const trigger = () => {
    if (started || printWindow.closed) return;
    started = true;
    window.setTimeout(() => {
      try {
        frame?.contentWindow?.focus();
        frame?.contentWindow?.print();
      } catch {
        try {
          printWindow.focus();
          printWindow.print();
        } catch {
          /* The document is still on screen if the dialog could not be opened. */
        }
      }
    }, 400);
  };
  try {
    frame?.addEventListener('load', trigger);
  } catch {
    /* Fall through to the timed trigger. */
  }
  window.setTimeout(trigger, 1200);
  window.setTimeout(() => {
    try {
      URL.revokeObjectURL(url);
    } catch {
      /* ignore */
    }
  }, 120_000);
  return Promise.resolve({
    opened: true,
    printed: true,
    popupBlocked: false,
    downloadFallback: false,
  });
}

export function preloadPrintWindow() {
  if (typeof window === 'undefined') return;
  try {
    const w = window.open('', '_blank', 'noopener,noreferrer');
    if (w) w.close();
  } catch {
    /* ignore popup blockers */
  }
}
